import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronLeft, ChevronRight, Link2, Loader2, Search } from 'lucide-react';
import { Modal } from '../../../reusable';
import {
  getTreasuryDocumentCandidates,
  getTreasuryDocumentSuggestions,
  setTreasuryDocumentLink,
} from '../../../../services/treasuryAPI';
import {
  addMonths,
  endOfMonth,
  isDateInRange,
  startOfMonth,
  startOfYear,
} from '../../../placeholders/periodSummaryUtils';

const money = (value) =>
  new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const formatDate = (value) => {
  const iso = String(value || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return '—';
  const [y, m, d] = iso.split('-');
  return `${d}-${m}-${y}`;
};

const scoreColor = (score) => {
  if (score >= 70) return '#16a34a';
  if (score >= 40) return '#d97706';
  return '#dc2626';
};

const CHECK_LABELS = {
  rut: { match: 'Coincide con la glosa', miss: 'Distinto', na: 'Sin RUT' },
  name: { match: 'Aparece en el movimiento', miss: 'No aparece', na: 'Sin dato' },
  amount: { exact: 'Exacto (total o neto)', near: 'Cercano', miss: 'Distinto' },
  date: { match: 'Dentro de 3 días', near: 'Dentro de 45 días', miss: 'Más de 45 días', na: 'Sin fecha' },
};

const percent = (value) => Math.max(0, Math.min(100, Math.round(Number(value || 0))));

const checkTone = (value) => {
  if (value === 'match' || value === 'exact') return '#16a34a';
  if (value === 'near') return '#d97706';
  if (value === 'miss') return '#dc2626';
  return '#94a3b8';
};

const RESULT_PRESENTATION = {
  linked: { label: 'Vinculado', color: '#16a34a' },
  skipped: { label: 'Omitido', color: '#64748b' },
};

const TYPE_LABELS = { income: 'Ingreso', expense: 'Egreso' };

const TYPE_FILTER_OPTIONS = [
  { value: 'all', label: 'Todos' },
  { value: 'income', label: 'Ingresos' },
  { value: 'expense', label: 'Egresos' },
];

const MATCH_FILTER_OPTIONS = [
  { value: 0, label: 'Cualquier coincidencia' },
  { value: 25, label: '25% o más' },
  { value: 50, label: '50% o más' },
  { value: 70, label: '70% o más' },
  { value: 90, label: '90% o más' },
];

const RECONCILE_PERIOD_OPTIONS = [
  { value: 'current_month', label: 'Mes actual' },
  { value: 'previous_month', label: 'Último mes' },
  { value: 'last_3', label: 'Últimos 3 meses' },
  { value: 'last_6', label: 'Últimos 6 meses' },
  { value: 'last_12', label: 'Últimos 12 meses' },
  { value: 'last_24', label: 'Últimos 24 meses' },
  { value: 'last_36', label: 'Últimos 36 meses' },
  { value: 'ytd', label: 'Año en curso' },
  { value: 'all', label: 'Todos los períodos' },
];

const LAST_MONTHS = { last_3: 3, last_6: 6, last_12: 12, last_24: 24, last_36: 36 };

const periodRangeFor = (period) => {
  if (!period || period === 'all') return null;
  const now = new Date();
  const currentMonthStart = startOfMonth(now);
  if (period === 'current_month') return { start: currentMonthStart, end: endOfMonth(now) };
  if (period === 'previous_month') {
    const start = addMonths(currentMonthStart, -1);
    return { start, end: endOfMonth(start) };
  }
  if (period === 'ytd') return { start: startOfYear(now.getFullYear()), end: endOfMonth(now) };
  const months = LAST_MONTHS[period];
  if (months) return { start: addMonths(currentMonthStart, -(months - 1)), end: endOfMonth(now) };
  return null;
};

const rowInPeriod = (row, period) => {
  const range = periodRangeFor(period);
  if (!range) return true;
  return isDateInRange(row.entry_date, range);
};

const documentLabel = (doc) => {
  if (!doc) return '—';
  const type = doc.document_type || doc.register_type_label || '';
  return `${type} ${doc.doc_number || ''}`.trim() || doc.doc_code || 'Documento';
};

/**
 * Conciliación tesorería ↔ contabilidad: movimiento bancario a la izquierda,
 * documento del libro (sugerido o elegido) a la derecha.
 */
const TreasuryBankReconcileModal = ({
  isOpen = false,
  onClose,
  rows = [],
  isDarkMode = false,
  ui,
  initialEntryId = null,
}) => {
  const [queue, setQueue] = useState([]);
  const [index, setIndex] = useState(0);
  const [results, setResults] = useState({});
  const [isLinking, setIsLinking] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [candidates, setCandidates] = useState([]);
  const [isLoadingCandidates, setIsLoadingCandidates] = useState(false);
  const [selectedDocId, setSelectedDocId] = useState(null);
  const [typeFilter, setTypeFilter] = useState('all');
  const [matchMin, setMatchMin] = useState(0);
  const [periodFilter, setPeriodFilter] = useState('last_36');
  const listRef = useRef(null);
  const searchTimer = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    const pending = rows
      .filter((row) => (row.reconciliation_status || 'Pendiente') !== 'Conciliado' || row.has_pending_document_match)
      .filter((row) => !row.register_document_id)
      .sort(
        (a, b) =>
          String(a.entry_date || '').localeCompare(String(b.entry_date || '')) || Number(a.id) - Number(b.id)
      );
    const target = pending.find((row) => Number(row.id) === Number(initialEntryId));
    const nextPeriod =
      target && !rowInPeriod(target, 'last_36') ? 'all' : 'last_36';
    setTypeFilter('all');
    setMatchMin(0);
    setPeriodFilter(nextPeriod);
    setQueue(pending);
    const scoped = pending.filter((row) => rowInPeriod(row, nextPeriod));
    const start = Math.max(
      0,
      scoped.findIndex((row) => Number(row.id) === Number(initialEntryId))
    );
    setIndex(start === -1 ? 0 : start);
    setResults({});
    setError('');
    setSearch('');
    setCandidates([]);
    setSelectedDocId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const scopedQueue = useMemo(
    () =>
      queue.filter((row) => {
        if (typeFilter !== 'all' && row.entry_type !== typeFilter) return false;
        return rowInPeriod(row, periodFilter);
      }),
    [queue, typeFilter, periodFilter]
  );

  const visibleQueue = useMemo(
    () =>
      scopedQueue.filter((row) => {
        if (matchMin <= 0) return true;
        return percent(row.suggested_match_score) >= matchMin;
      }),
    [scopedQueue, matchMin]
  );

  const current = visibleQueue[index] || null;

  useEffect(() => {
    if (index < visibleQueue.length) return;
    setIndex(0);
  }, [index, visibleQueue.length]);

  const suggestionIds = useMemo(
    () => scopedQueue.map((row) => row.id).filter(Boolean).slice(0, 250),
    [scopedQueue]
  );
  const suggestionIdsKey = suggestionIds.join(',');

  useEffect(() => {
    if (!isOpen || !suggestionIdsKey) return undefined;
    const ids = suggestionIdsKey.split(',').map((value) => Number(value)).filter(Boolean);
    let cancelled = false;
    getTreasuryDocumentSuggestions(ids)
      .then((payload) => {
        if (cancelled || !Array.isArray(payload) || !payload.length) return;
        const hints = new Map(payload.map((item) => [Number(item.id), item]));
        setQueue((prev) =>
          prev.map((row) => {
            const hint = hints.get(Number(row.id));
            return hint ? { ...row, ...hint } : row;
          })
        );
      })
      .catch(() => {
        /* el listado sigue usable sin sugerencias previas */
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, suggestionIdsKey]);

  useEffect(() => {
    const node = listRef.current?.querySelector(`[data-row-index="${index}"]`);
    node?.scrollIntoView?.({ block: 'nearest' });
  }, [index]);

  useEffect(() => {
    if (!isOpen || !current?.id) {
      setCandidates([]);
      setSelectedDocId(null);
      return undefined;
    }
    const suggestedId = Number(current.suggested_document_id || current.suggested_document?.id || 0) || null;
    setSelectedDocId(suggestedId);
    setIsLoadingCandidates(true);
    let cancelled = false;
    const load = async (query) => {
      try {
        const payload = await getTreasuryDocumentCandidates(current.id, query);
        if (cancelled) return;
        setCandidates(Array.isArray(payload) ? payload : []);
      } catch (err) {
        if (!cancelled) setError(err?.message || 'No se pudieron cargar los documentos.');
      } finally {
        if (!cancelled) setIsLoadingCandidates(false);
      }
    };
    load(search);
    return () => {
      cancelled = true;
    };
  }, [isOpen, current?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const requestCandidates = (query) => {
    if (!current?.id) return;
    setIsLoadingCandidates(true);
    getTreasuryDocumentCandidates(current.id, query)
      .then((payload) => setCandidates(Array.isArray(payload) ? payload : []))
      .catch((err) => setError(err?.message || 'No se pudieron cargar los documentos.'))
      .finally(() => setIsLoadingCandidates(false));
  };

  const handleSearchChange = (value) => {
    setSearch(value);
    window.clearTimeout(searchTimer.current);
    searchTimer.current = window.setTimeout(() => requestCandidates(value), 280);
  };

  const selectedDoc = useMemo(() => {
    if (!selectedDocId) return current?.suggested_document || null;
    return (
      candidates.find((doc) => Number(doc.id) === Number(selectedDocId)) ||
      (Number(current?.suggested_document?.id) === Number(selectedDocId) ? current.suggested_document : null)
    );
  }, [candidates, current, selectedDocId]);

  const counts = useMemo(() => {
    const values = Object.values(results);
    return {
      linked: values.filter((value) => value === 'linked').length,
      skipped: values.filter((value) => value === 'skipped').length,
    };
  }, [results]);

  const text = ui?.text || (isDarkMode ? '#f4f4f5' : '#111827');
  const muted = ui?.muted || (isDarkMode ? '#a1a1aa' : '#6b7280');
  const border = ui?.border || (isDarkMode ? '#3f3f46' : '#e5e7eb');
  const headerBg = ui?.header || (isDarkMode ? '#18181b' : '#f4f4f5');
  const selectedBg = isDarkMode ? 'rgba(37, 99, 235, 0.18)' : '#eff6ff';
  const inputBg = ui?.inputBg || 'transparent';
  const inputBorder = ui?.inputBorder || border;
  const total = visibleQueue.length;
  const reviewed = visibleQueue.filter((row) => results[row.id]).length;
  const allReviewed = total > 0 && reviewed >= total;
  const currentResult = current ? results[current.id] : null;
  const score = percent(selectedDoc?.match_score || current?.suggested_match_score);
  const checks = selectedDoc?.match_checks || current?.suggested_match_checks || {};

  const nextPendingIndex = (fromIndex, nextResults) => {
    for (let step = 1; step <= total; step += 1) {
      const candidate = (fromIndex + step) % total;
      const row = visibleQueue[candidate];
      if (row && !nextResults[row.id]) return candidate;
    }
    return fromIndex;
  };

  const changeFilter = (setter) => (value) => {
    setter(value);
    setIndex(0);
    setError('');
  };

  const handleSkip = () => {
    if (!current) return;
    setError('');
    if (currentResult) {
      setIndex(Math.min(total - 1, index + 1));
      return;
    }
    const nextResults = { ...results, [current.id]: 'skipped' };
    setResults(nextResults);
    setIndex(nextPendingIndex(index, nextResults));
  };

  const handleLink = async () => {
    if (!current || currentResult === 'linked') return;
    if (!selectedDoc?.id) {
      setError('Selecciona un documento contable para vincular.');
      return;
    }
    try {
      setIsLinking(true);
      setError('');
      await setTreasuryDocumentLink(current.id, {
        register_document_id: Number(selectedDoc.id),
        link_method: Number(current.suggested_document_id) === Number(selectedDoc.id) ? 'suggested' : 'manual',
        match_score: Number(selectedDoc.match_score || current.suggested_match_score || 0) || null,
        notes: '',
      });
      const nextResults = { ...results, [current.id]: 'linked' };
      setResults(nextResults);
      setIndex(nextPendingIndex(index, nextResults));
    } catch (err) {
      setError(err?.message || 'No se pudo vincular el movimiento.');
    } finally {
      setIsLinking(false);
    }
  };

  const handleClose = () => {
    if (isLinking) return;
    onClose?.({ linked: counts.linked });
  };

  const btn = (variant, enabled = true) => {
    const filled = variant === 'success';
    return {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      height: 36,
      padding: '0 14px',
      borderRadius: 8,
      border: filled ? 'none' : `1px solid ${inputBorder}`,
      background: filled ? '#16a34a' : inputBg,
      color: filled ? '#fff' : text,
      fontWeight: 600,
      fontSize: 13,
      cursor: enabled ? 'pointer' : 'not-allowed',
      opacity: enabled ? 1 : 0.5,
    };
  };

  const box = {
    border: `1px solid ${border}`,
    borderRadius: 10,
    minWidth: 0,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  };
  const boxHeader = {
    padding: '10px 12px',
    borderBottom: `1px solid ${border}`,
    background: headerBg,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
  };
  const th = (align = 'left') => ({
    position: 'sticky',
    top: 0,
    background: headerBg,
    textAlign: align,
    padding: '8px 10px',
    fontSize: 11,
    fontWeight: 700,
    color: muted,
    borderBottom: `1px solid ${border}`,
    whiteSpace: 'nowrap',
    zIndex: 1,
  });
  const td = (align = 'left') => ({
    textAlign: align,
    padding: '8px 10px',
    fontSize: 12,
    color: text,
    borderTop: `1px solid ${border}`,
    verticalAlign: 'top',
  });

  const filterSelect = {
    width: '100%',
    height: 32,
    padding: '0 8px',
    borderRadius: 8,
    border: `1px solid ${inputBorder}`,
    background: inputBg,
    color: text,
    fontSize: 12,
    boxSizing: 'border-box',
  };

  const renderList = () => (
    <section style={box}>
      <div style={boxHeader}>
        <h4 style={{ margin: 0, fontSize: 13, fontWeight: 700, color: text }}>Movimientos bancarios</h4>
        <span style={{ fontSize: 12, color: muted }}>
          {reviewed} de {total} revisado(s)
        </span>
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 0.85fr) minmax(0, 1.1fr) minmax(0, 1.15fr)',
          gap: 8,
          padding: '8px 12px',
          borderBottom: `1px solid ${border}`,
        }}
      >
        <label style={{ display: 'grid', gap: 4, minWidth: 0 }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: muted }}>Tipo</span>
          <select
            value={typeFilter}
            onChange={(event) => changeFilter(setTypeFilter)(event.target.value)}
            style={filterSelect}
          >
            {TYPE_FILTER_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <label style={{ display: 'grid', gap: 4, minWidth: 0 }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: muted }}>Coincidencia</span>
          <select
            value={String(matchMin)}
            onChange={(event) => changeFilter(setMatchMin)(Number(event.target.value))}
            style={filterSelect}
          >
            {MATCH_FILTER_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <label style={{ display: 'grid', gap: 4, minWidth: 0 }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: muted }}>Período</span>
          <select
            value={periodFilter}
            onChange={(event) => changeFilter(setPeriodFilter)(event.target.value)}
            style={filterSelect}
          >
            {RECONCILE_PERIOD_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div ref={listRef} style={{ overflowX: 'hidden', overflowY: 'auto', flex: 1 }}>
        {visibleQueue.length ? (
          <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
            <thead>
              <tr>
                <th style={{ ...th(), width: 142 }}>Movimiento</th>
                <th style={{ ...th(), width: 84 }}>Fecha</th>
                <th style={th()}>Descripción</th>
                <th style={{ ...th('right'), width: 96 }}>Monto</th>
                <th style={{ ...th(), width: 148 }}>Sugerido</th>
                <th style={{ ...th('right'), width: 44 }}>%</th>
              </tr>
            </thead>
            <tbody>
              {visibleQueue.map((row, rowIndex) => {
                const isSelected = rowIndex === index;
                const result = RESULT_PRESENTATION[results[row.id]];
                const rowScore = percent(row.suggested_match_score);
                const suggested = row.suggested_document;
                const movementLabel = `${TYPE_LABELS[row.entry_type] || row.entry_type} ${row.entry_code || ''}`.trim();
                return (
                  <tr
                    key={row.id}
                    data-row-index={rowIndex}
                    onClick={() => {
                      if (isLinking) return;
                      setError('');
                      setSearch('');
                      setIndex(rowIndex);
                    }}
                    style={{
                      cursor: isLinking ? 'wait' : 'pointer',
                      background: isSelected ? selectedBg : 'transparent',
                      boxShadow: isSelected ? 'inset 3px 0 0 #2563eb' : 'none',
                      opacity: result && !isSelected ? 0.6 : 1,
                    }}
                  >
                    <td style={td()}>
                      <div
                        style={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        title={movementLabel}
                      >
                        {movementLabel}
                      </div>
                      {result ? (
                        <div style={{ fontSize: 11, fontWeight: 700, color: result.color }}>{result.label}</div>
                      ) : null}
                    </td>
                    <td style={{ ...td(), whiteSpace: 'nowrap', color: muted }}>{formatDate(row.entry_date)}</td>
                    <td style={td()}>
                      <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.description || ''}>
                        {row.description || '—'}
                      </div>
                    </td>
                    <td style={{ ...td('right'), whiteSpace: 'nowrap', fontWeight: 700 }}>
                      {money(row.amount)}
                    </td>
                    <td style={{ ...td(), fontWeight: 600 }}>
                      <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={suggested ? documentLabel(suggested) : ''}>
                        {suggested ? documentLabel(suggested) : '—'}
                      </div>
                    </td>
                    <td style={{ ...td('right'), fontWeight: 700, color: scoreColor(rowScore) }}>
                      {row.has_pending_document_match ? `${rowScore}%` : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <p style={{ margin: 0, padding: 16, fontSize: 12, color: muted }}>
            Ningún movimiento coincide con los filtros.
          </p>
        )}
      </div>
    </section>
  );

  const renderComparison = () => {
    if (!current) return null;
    const result = RESULT_PRESENTATION[currentResult];
    const check = (key) => {
      const value = checks[key] || 'na';
      return (
        <span style={{ fontWeight: 700, color: checkTone(value), whiteSpace: 'nowrap' }}>
          {CHECK_LABELS[key][value] || 'Sin dato'}
        </span>
      );
    };
    const comparisonRows = [
      {
        label: 'Libro',
        bank: current.entry_type === 'income' ? 'Ingreso bancario' : 'Egreso bancario',
        doc: selectedDoc?.register_type_label || selectedDoc?.register_type || '—',
        result: null,
      },
      {
        label: 'Tipo',
        bank: TYPE_LABELS[current.entry_type] || current.entry_type,
        doc: selectedDoc?.document_type || '—',
        result: null,
      },
      {
        label: 'Fecha',
        bank: formatDate(current.entry_date),
        doc: formatDate(selectedDoc?.doc_date),
        result: check('date'),
      },
      {
        label: 'RUT',
        bank: (current.extracted_ruts || []).join(' · ') || '—',
        doc: selectedDoc?.counterpart_rut || '—',
        result: check('rut'),
      },
      {
        label: 'Contraparte',
        bank: current.description || '—',
        doc: selectedDoc?.counterpart_name || '—',
        result: check('name'),
      },
      {
        label: 'Monto',
        bank: money(current.amount),
        doc: selectedDoc ? money(selectedDoc.total_amount) : '—',
        result: check('amount'),
        strong: true,
      },
    ];
    return (
      <section style={box}>
        <div style={boxHeader}>
          <div style={{ minWidth: 0 }}>
            <h4 style={{ margin: 0, fontSize: 13, fontWeight: 700, color: text }}>
              {current.entry_code || 'Movimiento'} ↔ {selectedDoc ? documentLabel(selectedDoc) : 'Documento contable'}
            </h4>
            {result ? (
              <span style={{ fontSize: 12, fontWeight: 700, color: result.color }}>{result.label}</span>
            ) : (
              <span style={{ fontSize: 12, color: muted }}>
                {selectedDoc ? 'Pendiente de revisión' : 'Elige un documento de contabilidad'}
              </span>
            )}
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: muted }}>Coincidencia</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: scoreColor(score), lineHeight: 1.1 }}>
              {selectedDoc ? `${score}%` : '—'}
            </div>
          </div>
        </div>
        <div style={{ padding: '10px 12px', borderBottom: `1px solid ${border}` }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, position: 'relative' }}>
            <Search size={14} color={muted} style={{ position: 'absolute', left: 10 }} />
            <input
              value={search}
              onChange={(event) => handleSearchChange(event.target.value)}
              placeholder="Buscar folio, razón social o tipo de documento…"
              style={{
                width: '100%',
                height: 34,
                padding: '0 10px 0 30px',
                borderRadius: 8,
                border: `1px solid ${inputBorder}`,
                background: inputBg,
                color: text,
                fontSize: 13,
                boxSizing: 'border-box',
              }}
            />
          </label>
        </div>
        <div style={{ display: 'grid', gridTemplateRows: 'minmax(0, 1fr) minmax(140px, 0.9fr)', minHeight: 0, flex: 1 }}>
          <div style={{ overflowX: 'hidden', overflowY: 'auto', borderBottom: `1px solid ${border}` }}>
            {isLoadingCandidates ? (
              <p style={{ margin: 0, padding: 16, fontSize: 12, color: muted }}>Buscando documentos…</p>
            ) : candidates.length ? (
              <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                <thead>
                  <tr>
                    <th style={{ ...th(), width: '28%' }}>Documento</th>
                    <th style={{ ...th(), width: 118 }}>Libro</th>
                    <th style={{ ...th(), width: 84 }}>Fecha</th>
                    <th style={th()}>Contraparte</th>
                    <th style={{ ...th('right'), width: 96 }}>Total</th>
                    <th style={{ ...th('right'), width: 44 }}>%</th>
                  </tr>
                </thead>
                <tbody>
                  {candidates.slice(0, 40).map((doc) => {
                    const active = Number(doc.id) === Number(selectedDocId);
                    const bookLabel = doc.register_type_label || doc.register_type || '—';
                    return (
                      <tr
                        key={doc.id}
                        onClick={() => !isLinking && setSelectedDocId(doc.id)}
                        style={{
                          cursor: isLinking ? 'wait' : 'pointer',
                          background: active ? selectedBg : 'transparent',
                        }}
                      >
                        <td style={td()}>
                          <div style={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={documentLabel(doc)}>
                            {documentLabel(doc)}
                          </div>
                        </td>
                        <td style={td()}>
                          <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={bookLabel}>
                            {bookLabel}
                          </div>
                        </td>
                        <td style={{ ...td(), whiteSpace: 'nowrap', color: muted }}>{formatDate(doc.doc_date)}</td>
                        <td style={td()}>
                          <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={doc.counterpart_name || ''}>
                            {doc.counterpart_name || '—'}
                          </div>
                        </td>
                        <td style={{ ...td('right'), whiteSpace: 'nowrap' }}>{money(doc.total_amount)}</td>
                        <td style={{ ...td('right'), fontWeight: 700, color: scoreColor(doc.match_score) }}>
                          {percent(doc.match_score)}%
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <p style={{ margin: 0, padding: 16, fontSize: 12, color: muted }}>
                No hay documentos de {current.entry_type === 'income' ? 'ventas' : 'compras, honorarios o rendiciones'}{' '}
                cerca de esta fecha. Prueba buscando el folio.
              </p>
            )}
          </div>
          <div style={{ overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={th()}>Campo</th>
                  <th style={th()}>Movimiento bancario</th>
                  <th style={th()}>Documento contable</th>
                  <th style={th()}>Resultado</th>
                </tr>
              </thead>
              <tbody>
                {comparisonRows.map((row) => (
                  <tr key={row.label}>
                    <td style={{ ...td(), color: muted, fontWeight: 600, whiteSpace: 'nowrap' }}>{row.label}</td>
                    <td style={{ ...td(), fontWeight: row.strong ? 700 : 500 }}>{row.bank || '—'}</td>
                    <td style={{ ...td(), fontWeight: row.strong ? 700 : 500 }}>{row.doc || '—'}</td>
                    <td style={td()}>{row.result || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Conciliar movimientos bancarios"
      subtitle="Vincula cartola y caja con facturas, compras u otros documentos de Contabilidad."
      size="xxl"
      style={{ width: '1760px', maxWidth: '96vw' }}
      isDarkMode={isDarkMode}
      closeOnOverlayClick={!isLinking}
      closeOnEscape={!isLinking}
      footer={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
          <span style={{ fontSize: 12, color: allReviewed ? '#16a34a' : muted, fontWeight: allReviewed ? 700 : 400 }}>
            {allReviewed ? 'Conciliación terminada · ' : ''}
            {counts.linked} vinculado(s) · {counts.skipped} omitido(s) · {total - reviewed} pendiente(s)
          </span>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            {current ? (
              <>
                <button
                  type="button"
                  onClick={() => setIndex((value) => Math.max(0, value - 1))}
                  disabled={isLinking || index === 0}
                  style={btn('ghost', !isLinking && index > 0)}
                >
                  <ChevronLeft size={16} /> Anterior
                </button>
                <button
                  type="button"
                  onClick={handleSkip}
                  disabled={isLinking || (currentResult && index >= total - 1)}
                  style={btn('ghost', !isLinking && !(currentResult && index >= total - 1))}
                >
                  {currentResult ? 'Siguiente' : 'Omitir'} <ChevronRight size={16} />
                </button>
                <button
                  type="button"
                  onClick={handleLink}
                  disabled={isLinking || currentResult === 'linked' || !selectedDoc}
                  style={btn('success', !isLinking && currentResult !== 'linked' && Boolean(selectedDoc))}
                >
                  {isLinking ? <Loader2 size={16} /> : currentResult === 'linked' ? <Check size={16} /> : <Link2 size={16} />}
                  {isLinking
                    ? 'Vinculando…'
                    : currentResult === 'linked'
                      ? 'Vinculado'
                      : selectedDoc
                        ? `Vincular con ${documentLabel(selectedDoc)}`
                        : 'Selecciona un documento'}
                </button>
              </>
            ) : null}
            <button type="button" onClick={handleClose} disabled={isLinking} style={btn('ghost', !isLinking)}>
              Cerrar
            </button>
          </div>
        </div>
      }
    >
      {queue.length ? (
        <div style={{ display: 'grid', gap: 10 }}>
          <div style={{ height: 6, borderRadius: 999, background: border, overflow: 'hidden' }}>
            <div
              style={{
                width: `${total ? (reviewed / total) * 100 : 0}%`,
                height: '100%',
                background: '#16a34a',
                transition: 'width 0.2s',
              }}
            />
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 1.15fr) minmax(0, 1fr)',
              gap: 12,
              height: '64vh',
              minHeight: 380,
            }}
          >
            {renderList()}
            {renderComparison()}
          </div>
          {error ? <p style={{ margin: 0, fontSize: 13, color: ui?.danger || '#dc2626' }}>{error}</p> : null}
        </div>
      ) : (
        <p style={{ margin: 0, fontSize: 13, color: muted, padding: '24px 0', textAlign: 'center' }}>
          No hay movimientos pendientes de conciliación en el período seleccionado.
        </p>
      )}
    </Modal>
  );
};

export default TreasuryBankReconcileModal;
